/**
 * Single owner of permission resolution for the session.
 *
 * The set resolved here serves both React UI (via `useAuthZ`) and route
 * filtering (via `useAuthZPermissions`), so org settings cannot reach one and
 * not the other. See docs/authz.md Usage → Gate React UI (useAuthZ).
 */
import { type ReactNode, useMemo, useRef } from "react";

import type { AuthUser } from "@models/AuthUser";

import { AuthZContext } from "./AuthZContext";
import type { AuthZSession } from "./authZSession";
import { can, emptyPermissionSet, resolvePermissions } from "./can";
import { type OrgPolicy, compileOrgPolicy, orgPolicyKey } from "./orgPolicy";
import type { PermissionId } from "./permissionIds";

type CompiledSession = {
  role: AuthUser["role"];
  policy: OrgPolicy;
};

export function AuthZProvider({
  session,
  children,
}: {
  session: AuthZSession;
  children: ReactNode;
}) {
  // structuralSharing is disabled on current-user, so every refetch yields a
  // new organization object for an identical payload. Fingerprint content into
  // sessionKey; keep the matching compile in a ref so useMemo does not close
  // over a render-local binding with incomplete deps.
  let sessionKey: string | null = null;
  let compiled: CompiledSession | null = null;
  if (session !== null) {
    const policy = compileOrgPolicy(session.organization);
    compiled = { role: session.role, policy };
    sessionKey = `${session.role}:${orgPolicyKey(policy)}`;
  }

  const compiledRef = useRef(compiled);
  compiledRef.current = compiled;

  const value = useMemo(() => {
    // sessionKey is the content fingerprint; reading it ties the memo to policy
    // changes without depending on organization identity. Payload is in the ref,
    // which is assigned with sessionKey in the same render.
    if (sessionKey === null) {
      const permissions = emptyPermissionSet();
      return {
        permissions,
        can: (permissionId: PermissionId) => can(permissions, permissionId),
      };
    }

    const { role, policy } = compiledRef.current!;
    const permissions = resolvePermissions({ role }, policy);
    return {
      permissions,
      can: (permissionId: PermissionId) => can(permissions, permissionId),
    };
  }, [sessionKey]);

  return (
    <AuthZContext.Provider value={value}>{children}</AuthZContext.Provider>
  );
}
